/**
 * Market-aware stock check for pre-order / add-to-cart / sold-out buttons.
 *
 * Liquid's `variant.inventory_quantity` is the total across every location, but
 * US and Canadian buyers are served from different ones, so Liquid alone can
 * show Add to Cart for a variant with no stock in the buyer's market. The
 * Storefront API answers per market via `@inContext(country:)`:
 *   - currentlyNotInStock: out of stock for the market but still sellable
 *     (continue-selling on) - the pre-order condition.
 *   - availableForSale: sellable at all in the market.
 * Both work tokenless. `quantityAvailable` does not, and is not needed.
 *
 * Markup contract: the server renders every state the variant can be in as a
 * `[data-pane]` child (preorder | cart | soldout), hides all but the one Liquid
 * chose, and puts the data attributes on the wrapper:
 *
 *   <div data-market-stock data-variant-id="123" data-country="US">
 *     <div data-pane="preorder" hidden>...</div>
 *     <div data-pane="cart">...</div>
 *   </div>
 *
 * Liquid's choice is the default and the fallback: on timeout, error or an
 * unknown variant nothing is touched. Wrappers are discovered by scanning, so
 * section re-renders in the theme editor and the collection grid's in-place
 * filtering are picked up without any caller wiring.
 */
(function () {
  if (window.FeinbackMarketStock) return;

  const ENDPOINT = '/api/2025-10/graphql.json';
  const TIMEOUT_MS = 2000;
  // nodes() accepts up to 250 ids; stay well inside it.
  const CHUNK_SIZE = 100;
  const QUERY =
    'query($ids: [ID!]!, $country: CountryCode!) @inContext(country: $country) {' +
    ' nodes(ids: $ids) { ... on ProductVariant { id availableForSale currentlyNotInStock } } }';

  // `${country}:${variantId}` -> { available, notInStock }. Only successful
  // answers are cached, so a failed request is retried on the next scan.
  const cache = {};

  async function request(ids, country) {
    const controller = new AbortController();
    // The abort covers reading the body as well as the connection.
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          query: QUERY,
          variables: { ids: ids.map((id) => 'gid://shopify/ProductVariant/' + id), country },
        }),
        signal: controller.signal,
      });
      if (!response.ok) return null;
      const json = await response.json();
      if (json.errors || !json.data || !Array.isArray(json.data.nodes)) return null;
      return json.data.nodes;
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Resolves { [variantId]: { available, notInStock } } for the variants the
   * API answered for. Variants missing from the result are unknown: callers
   * should leave their server-rendered state alone.
   */
  async function check(variantIds, country) {
    const result = {};
    const missing = [];
    variantIds.forEach((id) => {
      const hit = cache[country + ':' + id];
      if (hit) result[id] = hit;
      else if (missing.indexOf(id) === -1) missing.push(id);
    });

    const chunks = [];
    for (let i = 0; i < missing.length; i += CHUNK_SIZE) chunks.push(missing.slice(i, i + CHUNK_SIZE));

    const responses = await Promise.all(chunks.map((chunk) => request(chunk, country)));
    responses.forEach((nodes) => {
      if (!nodes) return;
      nodes.forEach((node) => {
        // A deleted or non-variant id comes back as null.
        if (!node || !node.id) return;
        const id = node.id.split('/').pop();
        const entry = { available: !!node.availableForSale, notInStock: !!node.currentlyNotInStock };
        cache[country + ':' + id] = entry;
        result[id] = entry;
      });
    });
    return result;
  }

  function stateFor(entry) {
    if (!entry.available) return 'soldout';
    return entry.notInStock ? 'preorder' : 'cart';
  }

  function setState(wrapper, state) {
    const panes = wrapper.querySelectorAll(':scope > [data-pane]');
    let target = null;
    panes.forEach((pane) => {
      if (pane.dataset.pane === state) target = pane;
    });
    // A deny-policy variant has no preorder pane; there is nothing to swap to.
    if (!target) return;

    panes.forEach((pane) => {
      const hide = pane !== target;
      pane.hidden = hide;
      // `hidden` stops the add-to-cart button being clicked or tabbed to, but a
      // hidden submit button is still the form's default, so Enter in the
      // quantity field would submit it. Disabling it closes that.
      pane.querySelectorAll('[data-atc]').forEach((button) => {
        button.disabled = hide;
      });
    });
    wrapper.dataset.state = state;
  }

  // Hold the wrapper at its current height while the check runs, so a swap to a
  // taller or shorter pane does not shove the content below it around.
  function hold(wrapper) {
    const height = wrapper.offsetHeight;
    if (height) wrapper.style.minHeight = height + 'px';
  }

  function release(wrapper) {
    wrapper.style.minHeight = '';
  }

  function scan() {
    const wrappers = document.querySelectorAll('[data-market-stock]:not([data-market-bound])');
    const byCountry = {};
    wrappers.forEach((wrapper) => {
      const { variantId, country } = wrapper.dataset;
      if (!variantId || !country) return;
      wrapper.setAttribute('data-market-bound', '');
      hold(wrapper);
      (byCountry[country] = byCountry[country] || []).push(wrapper);
    });

    Object.keys(byCountry).forEach((country) => {
      const group = byCountry[country];
      // One request per country covers every card on the page.
      check(group.map((wrapper) => wrapper.dataset.variantId), country).then((result) => {
        group.forEach((wrapper) => {
          const entry = result[wrapper.dataset.variantId];
          if (entry) setState(wrapper, stateFor(entry));
          release(wrapper);
        });
      });
    });
  }

  /**
   * Re-checks one wrapper, optionally for a different variant. The product page
   * reloads on variant change today, so nothing calls this yet.
   */
  function refresh(wrapper, variantId) {
    if (variantId) wrapper.dataset.variantId = String(variantId);
    wrapper.removeAttribute('data-market-bound');
    scan();
  }

  window.FeinbackMarketStock = { check, scan, refresh };

  scan();
  document.addEventListener('shopify:section:load', scan);

  // The collection grid swaps cards in with innerHTML, and sections can inject
  // more later; watch for new wrappers rather than hook every renderer.
  let pending;
  new MutationObserver(() => {
    clearTimeout(pending);
    pending = setTimeout(scan, 50);
  }).observe(document.body, { childList: true, subtree: true });
})();
